import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import { marked } from 'marked';
import RichTextEditor from '../components/RichTextEditor';
import './StaffHRPage.css';
import './CreateJobPage.css';

marked.setOptions({ breaks: true, gfm: true });

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatJobDateValue = (value) => {
  if (!value) return '';
  return String(value).split('T')[0];
};

const normalizeJobSections = (sections) => {
  if (!Array.isArray(sections)) return [];
  return sections.map((section) => ({
    label: section?.label || '',
    description: section?.description || '',
    questions: Array.isArray(section?.questions)
      ? section.questions.map((question) => ({
          label: question?.label || '',
          type: question?.type || 'textarea',
          required: Boolean(question?.required),
          options: Array.isArray(question?.options) ? question.options : [],
          placeholder: question?.placeholder || '',
          helpText: question?.helpText || '',
          isHtml: Boolean(question?.isHtml)
        }))
      : []
  }));
};

const buildJobJsonPayload = (job = {}) => ({
  title: job.title ?? '',
  department: job.department ?? '',
  location: job.location ?? '',
  employmentType: job.employmentType ?? '',
  salaryRange: job.salaryRange ?? '',
  applicationDeadline: formatJobDateValue(job.applicationDeadline),
  description: job.description ?? '',
  responsibilities: job.responsibilities ?? '',
  requirements: job.requirements ?? '',
  isPublished: Boolean(job.isPublished),
  isFeatured: Boolean(job.isFeatured),
  visibleTo: Array.isArray(job.visibleTo) && job.visibleTo.length ? job.visibleTo : ['public', 'user', 'citizen'],
  sections: normalizeJobSections(job.sections)
});

// ── Inline HTML editor for question labels ────────────────────────────────────

const HtmlEditor = ({ value, onChange }) => {
  const taRef = useRef(null);
  const [mode, setMode] = useState('html');
  const [preview, setPreview] = useState(false);
  const [mdText, setMdText] = useState('');

  const mdHtml = mode === 'markdown' ? marked.parse(mdText) : '';

  const applyMarkdown = () => {
    onChange(marked.parse(mdText).trim());
    setMode('html');
    setPreview(true);
  };

  const wrap = useCallback((before, after, placeholder) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.slice(start, end) || placeholder;
    const newVal = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(newVal);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + selected.length);
    }, 0);
  }, [value, onChange]);

  const insertBlock = useCallback((block) => {
    const ta = taRef.current;
    if (!ta) return;
    const pos = ta.selectionStart;
    const newVal = value.slice(0, pos) + '\n' + block + '\n' + value.slice(pos);
    onChange(newVal);
    setTimeout(() => ta.focus(), 0);
  }, [value, onChange]);

  const htmlTools = [
    { label: 'B', title: 'Bold', action: () => wrap('<strong>', '</strong>', 'Bold text') },
    { label: 'I', title: 'Italic', action: () => wrap('<em>', '</em>', 'Italic text') },
    { label: 'H3', title: 'Heading', action: () => wrap('<h3>', '</h3>', 'Heading') },
    { label: 'P', title: 'Paragraph', action: () => wrap('<p>', '</p>', 'Paragraph text') },
    { label: '• List', title: 'Bullet list', action: () => insertBlock('<ul>\n  <li>Item one</li>\n  <li>Item two</li>\n</ul>') },
    { label: '1. List', title: 'Numbered list', action: () => insertBlock('<ol>\n  <li>First</li>\n  <li>Second</li>\n</ol>') },
    { label: 'HR', title: 'Divider', action: () => insertBlock('<hr/>') },
  ];

  if (mode === 'markdown') {
    return (
      <div className="html-editor-wrapper">
        <div className="html-toolbar">
          <span className="html-toolbar-mode-label">Markdown →</span>
          <button type="button" className="html-tool-btn" title="Bold" onMouseDown={e => { e.preventDefault(); }}>**bold**</button>
          <button type="button" className="html-tool-btn" title="Italic" onMouseDown={e => { e.preventDefault(); }}>*italic*</button>
          <button type="button" className="html-tool-btn" title="Heading" onMouseDown={e => { e.preventDefault(); }}>## heading</button>
          <button type="button" className="html-tool-btn" title="Bullet" onMouseDown={e => { e.preventDefault(); }}>- item</button>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="html-tool-btn html-apply-md-btn"
            title="Convert markdown to HTML and use it"
            onMouseDown={e => { e.preventDefault(); applyMarkdown(); }}
          >
            ✓ Convert to HTML
          </button>
          <button
            type="button"
            className="html-tool-btn"
            title="Cancel markdown mode"
            onMouseDown={e => { e.preventDefault(); setMode('html'); }}
          >
            ✕ Cancel
          </button>
        </div>
        <div className="md-editor-split">
          <div className="md-editor-pane">
            <div className="md-pane-label">Markdown</div>
            <textarea
              className="html-label-editor md-textarea"
              value={mdText}
              onChange={e => setMdText(e.target.value)}
              rows="10"
              placeholder={`**Applicant Attestation**\n\nApplicants must confirm eligibility.\n\nThis includes:\n- Not currently engaged in legal disputes\n- No conflicts of interest\n- Commitment to acting in good faith`}
            />
          </div>
          <div className="md-preview-pane">
            <div className="md-pane-label">Preview</div>
            <div
              className="html-question-label md-preview-content"
              dangerouslySetInnerHTML={{ __html: mdHtml || '<em style="color:#9ca3af">Start typing markdown on the left…</em>' }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="html-editor-wrapper">
      <div className="html-toolbar">
        {htmlTools.map(t => (
          <button key={t.label} type="button" className="html-tool-btn" title={t.title} onMouseDown={e => { e.preventDefault(); t.action(); }}>
            {t.label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="html-tool-btn html-md-mode-btn"
          title="Switch to Markdown editor"
          onMouseDown={e => { e.preventDefault(); setMode('markdown'); }}
        >
          ↓ Markdown
        </button>
        <button
          type="button"
          className={`html-tool-btn html-preview-btn${preview ? ' active' : ''}`}
          title="Toggle HTML preview"
          onMouseDown={e => { e.preventDefault(); setPreview(p => !p); }}
        >
          {preview ? '✕ Preview' : '👁 Preview'}
        </button>
      </div>
      <textarea
        ref={taRef}
        className="html-label-editor"
        value={value}
        onChange={e => onChange(e.target.value)}
        rows="7"
        placeholder={`<p><strong>Applicant Attestation</strong></p>\n<p>Applicants must confirm...</p>\n<ul>\n  <li>Item one</li>\n  <li>Item two</li>\n</ul>`}
      />
      {preview && (
        <div className="html-live-preview">
          <div className="html-live-preview-label">Preview</div>
          <div className="html-question-label" dangerouslySetInnerHTML={{ __html: value || '<em style="color:#9ca3af">Nothing to preview yet</em>' }} />
        </div>
      )}
    </div>
  );
};

// ── JSON template ─────────────────────────────────────────────────────────────

const JSON_TEMPLATE = JSON.stringify({
  title: 'Lower House Council Member',
  department: 'Governance',
  location: 'Red Sky Métis Independent Nation',
  employmentType: 'Volunteer',
  salaryRange: '',
  applicationDeadline: '2026-06-01',
  description: 'We are seeking dedicated community members to serve on the RSMIN Lower House Council.',
  responsibilities: 'Attend scheduled meetings. Represent community voices. Participate in decisions.',
  requirements: 'Must be a recognized RSMIN citizen. Strong communication skills.',
  isPublished: false,
  isFeatured: false,
  visibleTo: ['public', 'user', 'citizen'],
  sections: [
    {
      label: 'Eligibility & Understanding',
      description: 'Please confirm your eligibility before proceeding.',
      questions: [
        {
          label: 'I confirm that I am a recognized citizen of the Red Sky Métis Independent Nation.',
          type: 'radio',
          required: true,
          options: ['Yes', 'No'],
          placeholder: '',
          helpText: ''
        },
        {
          label: 'I understand that the position is voluntary and may not include compensation.',
          type: 'radio',
          required: true,
          options: ['Yes', 'No'],
          placeholder: '',
          helpText: ''
        }
      ]
    },
    {
      label: 'Community & Culture',
      description: 'Tell us about your connection to RSMIN.',
      questions: [
        {
          label: 'What is your connection to RSMIN and the Red Sky Métis community?',
          type: 'textarea',
          required: true,
          options: [],
          placeholder: 'Describe your connection...',
          helpText: ''
        },
        {
          label: 'How familiar are you with RSMIN traditions, teachings, and cultural practices?',
          type: 'radio',
          required: true,
          options: ['Very Familiar', 'Somewhat Familiar', 'Limited Familiarity', 'Not Familiar Yet'],
          placeholder: '',
          helpText: ''
        }
      ]
    }
  ]
}, null, 2);

// ── Page component ────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  title: '',
  department: '',
  location: '',
  employmentType: '',
  salaryRange: '',
  applicationDeadline: '',
  description: '',
  requirements: '',
  responsibilities: '',
  isPublished: false,
  isFeatured: false,
  visibleTo: ['public', 'user', 'citizen'],
  sections: []
};

const CreateJobPage = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showJsonImport, setShowJsonImport] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [jobForm, setJobForm] = useState(EMPTY_FORM);

  const canAccess = currentUser && currentUser.hr;

  // Load existing job when editing
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/api/hr/jobs/${id}`)
      .then(res => {
        const job = res.data.job || res.data;
        setJobForm({
          title: job.title || '',
          department: job.department || '',
          location: job.location || '',
          employmentType: job.employmentType || '',
          salaryRange: job.salaryRange || '',
          applicationDeadline: job.applicationDeadline ? job.applicationDeadline.slice(0, 10) : '',
          description: job.description || '',
          requirements: job.requirements || '',
          responsibilities: job.responsibilities || '',
          isPublished: Boolean(job.isPublished),
          isFeatured: Boolean(job.isFeatured),
          visibleTo: job.visibleTo && job.visibleTo.length > 0 ? job.visibleTo : ['public', 'user', 'citizen'],
          sections: job.sections || []
        });
      })
      .catch(() => setError('Failed to load job posting.'))
      .finally(() => setLoading(false));
  }, [id]);

  const currentJobJson = useMemo(() => JSON.stringify(buildJobJsonPayload(jobForm), null, 2), [jobForm]);

  const handleJsonImport = () => {
    setJsonError('');
    try {
      const parsed = JSON.parse(jsonInput);
      setJobForm(prev => buildJobJsonPayload({
        ...prev,
        ...parsed,
        sections: parsed.sections ?? prev.sections,
        visibleTo: parsed.visibleTo ?? prev.visibleTo
      }));
      setShowJsonImport(false);
      setJsonInput('');
    } catch {
      setJsonError('Invalid JSON — please check the format and try again.');
    }
  };

  // ── Form handlers ───────────────────────────────────────────────────────────

  const addSection = () => {
    setJobForm(prev => ({
      ...prev,
      sections: [...prev.sections, { label: '', description: '', questions: [] }]
    }));
  };

  const removeSection = (idx) => {
    setJobForm(prev => ({ ...prev, sections: prev.sections.filter((_, i) => i !== idx) }));
  };

  const handleSectionChange = (idx, field, value) => {
    setJobForm(prev => {
      const sections = [...prev.sections];
      sections[idx] = { ...sections[idx], [field]: value };
      return { ...prev, sections };
    });
  };

  const addQuestion = (sIdx) => {
    setJobForm(prev => {
      const sections = [...prev.sections];
      sections[sIdx] = {
        ...sections[sIdx],
        questions: [
          ...sections[sIdx].questions,
          { label: '', type: 'textarea', required: false, placeholder: '', helpText: '', options: [], isHtml: false }
        ]
      };
      return { ...prev, sections };
    });
  };

  const removeQuestion = (sIdx, qIdx) => {
    setJobForm(prev => {
      const sections = [...prev.sections];
      sections[sIdx] = { ...sections[sIdx], questions: sections[sIdx].questions.filter((_, i) => i !== qIdx) };
      return { ...prev, sections };
    });
  };

  const moveQuestion = (sIdx, qIdx, direction) => {
    setJobForm(prev => {
      const sections = [...prev.sections];
      const questions = [...sections[sIdx].questions];
      const targetIdx = qIdx + direction;
      if (targetIdx < 0 || targetIdx >= questions.length) return prev;
      [questions[qIdx], questions[targetIdx]] = [questions[targetIdx], questions[qIdx]];
      sections[sIdx] = { ...sections[sIdx], questions };
      return { ...prev, sections };
    });
  };

  const handleQuestionChange = (sIdx, qIdx, field, value) => {
    setJobForm(prev => {
      const sections = [...prev.sections];
      const questions = [...sections[sIdx].questions];
      questions[qIdx] = { ...questions[qIdx], [field]: value };
      sections[sIdx] = { ...sections[sIdx], questions };
      return { ...prev, sections };
    });
  };

  const handleQuestionOptionsChange = (sIdx, qIdx, value) => {
    const options = value.split('\n').filter(o => o.trim());
    handleQuestionChange(sIdx, qIdx, 'options', options);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setJobForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleVisibleToChange = (tier) => {
    setJobForm(prev => ({
      ...prev,
      visibleTo: prev.visibleTo.includes(tier)
        ? prev.visibleTo.filter(t => t !== tier)
        : [...prev.visibleTo, tier]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (isEditing) {
        await api.put(`/api/hr/jobs/${id}`, jobForm);
      } else {
        await api.post('/api/hr/jobs', jobForm);
      }
      navigate('/staff/hr');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save job posting.');
    } finally {
      setLoading(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="staff-hr-page">
        <div className="error-message">You do not have HR access.</div>
      </div>
    );
  }

  return (
    <div className="staff-hr-page">
      {/* Page header */}
      <div className="page-header">
        <div className="create-job-breadcrumb">
          <button className="btn-back-link" onClick={() => navigate('/staff/hr')}>
            ← HR Dashboard
          </button>
        </div>
        <h1>{isEditing ? '✏️ Edit Job Posting' : '➕ Create Job Posting'}</h1>
        <p>{isEditing ? 'Update the details for this position.' : 'Fill out the form below to create a new job posting.'}</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* JSON Import Panel */}
      <div className="json-import-bar">
        <button
          type="button"
          className="btn-json-toggle"
          onClick={() => { setShowJsonImport(v => !v); setJsonError(''); }}
        >
          {showJsonImport ? '✕ Close JSON Import' : '{ } Import from JSON'}
        </button>
        {!showJsonImport && (
          <span className="json-bar-hint">Paste a job posting JSON structure to auto-fill the form, or copy the current job JSON and reload updates back into the form.</span>
        )}
      </div>

      {showJsonImport && (
        <div className="json-import-panel">
          <div className="json-import-header">
            <h3>📋 Edit Job Posting with JSON</h3>
            <p>Use the template or copy the current job JSON, update it, then paste it back and click <strong>Load into Form</strong>.</p>
          </div>
          <div className="json-template-block">
            <div className="json-template-label">
              <span>Example Template</span>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="button" className="btn-copy-json" onClick={() => navigator.clipboard.writeText(JSON_TEMPLATE)}>
                  📋 Copy Template
                </button>
                <button type="button" className="btn-copy-json" onClick={() => navigator.clipboard.writeText(currentJobJson)}>
                  📋 Copy Current JSON
                </button>
              </div>
            </div>
            <pre className="json-template-pre">{JSON_TEMPLATE}</pre>
          </div>
          <div className="json-template-block">
            <div className="json-template-label">
              <span>Current Job JSON</span>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="button" className="btn-copy-json" onClick={() => setJsonInput(currentJobJson)}>
                  ✏ Load Current into Editor
                </button>
                <button type="button" className="btn-copy-json" onClick={() => navigator.clipboard.writeText(currentJobJson)}>
                  📋 Copy Current JSON
                </button>
              </div>
            </div>
            <pre className="json-template-pre">{currentJobJson}</pre>
          </div>
          <div className="json-paste-block">
            <label htmlFor="json-paste">Paste your edited JSON here:</label>
            <textarea
              id="json-paste"
              className="json-paste-area"
              rows={14}
              value={jsonInput}
              onChange={e => { setJsonInput(e.target.value); setJsonError(''); }}
              placeholder="Paste your JSON here..."
              spellCheck={false}
            />
          </div>
          {jsonError && <div className="json-error">{jsonError}</div>}
          <div className="json-import-actions">
            <button type="button" className="btn-primary" onClick={handleJsonImport} disabled={!jsonInput.trim()}>
              ⬇ Load into Form
            </button>
            <button type="button" className="btn-secondary" onClick={() => { setShowJsonImport(false); setJsonInput(''); setJsonError(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Form */}
      <section className="job-form-section">
        <form onSubmit={handleSubmit} className="job-form">
          <div className="form-row">
            <div className="form-group">
              <label>Job Title *</label>
              <input name="title" value={jobForm.title} onChange={handleFormChange} required />
            </div>
            <div className="form-group">
              <label>Department</label>
              <input name="department" value={jobForm.department} onChange={handleFormChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Location</label>
              <input name="location" value={jobForm.location} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>Employment Type</label>
              <input name="employmentType" value={jobForm.employmentType} onChange={handleFormChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Salary Range</label>
              <input name="salaryRange" value={jobForm.salaryRange} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>Application Deadline</label>
              <input type="date" name="applicationDeadline" value={jobForm.applicationDeadline} onChange={handleFormChange} />
            </div>
          </div>
          <div className="form-group">
            <label>Description *</label>
            <RichTextEditor
              value={jobForm.description}
              onChange={(val) => setJobForm(prev => ({ ...prev, description: val }))}
              placeholder="Describe the role and its purpose…"
            />
          </div>
          <div className="form-group">
            <label>Responsibilities</label>
            <RichTextEditor
              value={jobForm.responsibilities}
              onChange={(val) => setJobForm(prev => ({ ...prev, responsibilities: val }))}
              placeholder="List the key responsibilities…"
            />
          </div>
          <div className="form-group">
            <label>Requirements</label>
            <RichTextEditor
              value={jobForm.requirements}
              onChange={(val) => setJobForm(prev => ({ ...prev, requirements: val }))}
              placeholder="List the requirements and qualifications…"
            />
          </div>

          {/* Application Form Builder */}
          <div className="form-section-divider">
            <div className="form-section-header">
              <h3>📋 Application Form Builder</h3>
              <p>Add custom sections and questions that applicants must complete when applying.</p>
              <button type="button" className="btn-add-section" onClick={addSection}>
                + Add Section
              </button>
            </div>

            {jobForm.sections.map((section, sIdx) => (
              <div key={sIdx} className="section-builder">
                <div className="section-builder-header">
                  <h4>Section {sIdx + 1}</h4>
                  <button type="button" className="btn-remove" onClick={() => removeSection(sIdx)}>
                    Remove Section
                  </button>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Section Title *</label>
                    <input
                      type="text"
                      value={section.label}
                      onChange={(e) => handleSectionChange(sIdx, 'label', e.target.value)}
                      placeholder="e.g., Eligibility & Understanding"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      value={section.description}
                      onChange={(e) => handleSectionChange(sIdx, 'description', e.target.value)}
                      placeholder="Brief description for applicants"
                    />
                  </div>
                </div>

                <div className="questions-section">
                  <div className="questions-header">
                    <h5>Questions</h5>
                    <button type="button" className="btn-add-question" onClick={() => addQuestion(sIdx)}>
                      + Add Question
                    </button>
                  </div>

                  {section.questions.map((question, qIdx) => (
                    <div key={qIdx} className="question-builder">
                      <div className="question-builder-header">
                        <span>Q{qIdx + 1}</span>
                        <div className="question-header-actions">
                          <button
                            type="button"
                            className="btn-move-question"
                            onClick={() => moveQuestion(sIdx, qIdx, -1)}
                            disabled={qIdx === 0}
                            title="Move up"
                          >▲</button>
                          <button
                            type="button"
                            className="btn-move-question"
                            onClick={() => moveQuestion(sIdx, qIdx, 1)}
                            disabled={qIdx === section.questions.length - 1}
                            title="Move down"
                          >▼</button>
                          <button type="button" className="btn-remove-small" onClick={() => removeQuestion(sIdx, qIdx)}>
                            Remove
                          </button>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <div className="question-label-header">
                            <label>Question Text *</label>
                            <label className="html-toggle-label">
                              <input
                                type="checkbox"
                                checked={!!question.isHtml}
                                onChange={(e) => handleQuestionChange(sIdx, qIdx, 'isHtml', e.target.checked)}
                              />
                              HTML mode
                            </label>
                          </div>
                          {question.isHtml ? (
                            <HtmlEditor
                              value={question.label}
                              onChange={(val) => handleQuestionChange(sIdx, qIdx, 'label', val)}
                            />
                          ) : (
                            <input
                              type="text"
                              value={question.label}
                              onChange={(e) => handleQuestionChange(sIdx, qIdx, 'label', e.target.value)}
                              placeholder="Enter question text"
                              required
                            />
                          )}
                        </div>
                        <div className="form-group">
                          <label>Type *</label>
                          <select
                            value={question.type}
                            onChange={(e) => handleQuestionChange(sIdx, qIdx, 'type', e.target.value)}
                          >
                            <option value="textarea">Long Text</option>
                            <option value="text">Short Text</option>
                            <option value="email">Email</option>
                            <option value="date">Date</option>
                            <option value="radio">Radio (Single Choice)</option>
                            <option value="checkbox">Checkbox (Agreement)</option>
                            <option value="dropdown">Dropdown</option>
                            <option value="multi-select">Multi-Select</option>
                            <option value="file">File Upload</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Placeholder</label>
                          <input
                            type="text"
                            value={question.placeholder}
                            onChange={(e) => handleQuestionChange(sIdx, qIdx, 'placeholder', e.target.value)}
                            placeholder="Optional hint text"
                          />
                        </div>
                        <div className="form-group">
                          <label>Help Text</label>
                          <input
                            type="text"
                            value={question.helpText}
                            onChange={(e) => handleQuestionChange(sIdx, qIdx, 'helpText', e.target.value)}
                            placeholder="Additional guidance for applicants"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={question.required}
                            onChange={(e) => handleQuestionChange(sIdx, qIdx, 'required', e.target.checked)}
                          />
                          Required question
                        </label>
                      </div>

                      {(question.type === 'radio' || question.type === 'dropdown' || question.type === 'multi-select') && (
                        <div className="form-group">
                          <label>Options (one per line) *</label>
                          <textarea
                            value={question.options.join('\n')}
                            onChange={(e) => handleQuestionOptionsChange(sIdx, qIdx, e.target.value)}
                            rows="3"
                            placeholder={'Yes\nNo'}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="form-row checkbox-row">
            <label>
              <input type="checkbox" name="isPublished" checked={jobForm.isPublished} onChange={handleFormChange} />
              Publish immediately
            </label>
            <label>
              <input type="checkbox" name="isFeatured" checked={jobForm.isFeatured} onChange={handleFormChange} />
              Feature this job
            </label>
          </div>
          <div className="form-group">
            <label>Visible To (Access Levels)</label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={jobForm.visibleTo.includes('public')}
                  onChange={() => handleVisibleToChange('public')}
                />
                🌍 Public (Not logged in visitors)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={jobForm.visibleTo.includes('user')}
                  onChange={() => handleVisibleToChange('user')}
                />
                👤 User (Logged in, not verified)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={jobForm.visibleTo.includes('citizen')}
                  onChange={() => handleVisibleToChange('citizen')}
                />
                ✓ Citizen (Verified with card number)
              </label>
            </div>
            <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
              Select which user tiers can view this job posting. Leave all checked for public access.
            </small>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving…' : isEditing ? 'Update Job' : 'Create Job'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => navigate('/staff/hr')}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-preview-toggle"
              onClick={() => setShowPreview(v => !v)}
            >
              {showPreview ? '✕ Hide Preview' : '👁 Preview Posting'}
            </button>
          </div>
        </form>
      </section>

      {/* Job Posting Preview */}
      {showPreview && (
        <section className="job-preview-section">
          <div className="job-preview-banner">👁 Live Preview — this is how applicants will see the posting</div>
          <div className="job-preview-card">
            <div className="job-preview-header">
              <div>
                <h2 className="job-preview-title">{jobForm.title || 'Untitled Job'}</h2>
                <p className="job-preview-org">Red Sky Métis Independent Nation</p>
              </div>
              <div className="job-preview-badges">
                {jobForm.isFeatured && <span className="preview-tag featured">⭐ Featured</span>}
                <span className={`preview-tag ${jobForm.isPublished ? 'published' : 'draft'}`}>
                  {jobForm.isPublished ? 'Published' : 'Draft'}
                </span>
              </div>
            </div>

            <div className="job-preview-meta">
              {jobForm.department && <span>🏢 {jobForm.department}</span>}
              {jobForm.location && <span>📍 {jobForm.location}</span>}
              {jobForm.employmentType && <span>⏱ {jobForm.employmentType}</span>}
              {jobForm.salaryRange && <span>💰 {jobForm.salaryRange}</span>}
              {jobForm.applicationDeadline && <span>📅 Deadline: {new Date(jobForm.applicationDeadline).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</span>}
            </div>

            {jobForm.description && (
              <div className="job-preview-block">
                <h3>About This Position</h3>
                <div className="job-detail-html" dangerouslySetInnerHTML={{ __html: jobForm.description }} />
              </div>
            )}
            {jobForm.responsibilities && (
              <div className="job-preview-block">
                <h3>Responsibilities</h3>
                <div className="job-detail-html" dangerouslySetInnerHTML={{ __html: jobForm.responsibilities }} />
              </div>
            )}
            {jobForm.requirements && (
              <div className="job-preview-block">
                <h3>Requirements</h3>
                <div className="job-detail-html" dangerouslySetInnerHTML={{ __html: jobForm.requirements }} />
              </div>
            )}

            {jobForm.sections && jobForm.sections.length > 0 && (
              <div className="job-preview-block">
                <h3>Application Form</h3>
                {jobForm.sections.map((section, sIdx) => (
                  <div key={sIdx} className="preview-section">
                    <h4 className="preview-section-title">{section.label || `Section ${sIdx + 1}`}</h4>
                    {section.description && <p className="preview-section-desc">{section.description}</p>}
                    {section.questions && section.questions.map((q, qIdx) => (
                      <div key={qIdx} className="preview-question">
                        {q.isHtml ? (
                          <div className="html-question-label" dangerouslySetInnerHTML={{ __html: q.label }} />
                        ) : (
                          <label className="preview-question-label">
                            {q.label}{q.required && <span className="preview-required"> *</span>}
                          </label>
                        )}
                        {q.helpText && <small className="preview-help">{q.helpText}</small>}
                        {(q.type === 'text' || q.type === 'email') && (
                          <input type={q.type} placeholder={q.placeholder || ''} disabled className="preview-input" />
                        )}
                        {q.type === 'textarea' && (
                          <textarea rows={3} placeholder={q.placeholder || ''} disabled className="preview-input" />
                        )}
                        {q.type === 'date' && <input type="date" disabled className="preview-input" />}
                        {q.type === 'radio' && (
                          <div className="preview-options">
                            {q.options.map(opt => (
                              <label key={opt} className="preview-option-label">
                                <input type="radio" disabled /> {opt}
                              </label>
                            ))}
                          </div>
                        )}
                        {q.type === 'checkbox' && (
                          <label className="preview-option-label">
                            <input type="checkbox" disabled /> {q.isHtml ? 'I confirm the above' : 'I agree'}
                          </label>
                        )}
                        {q.type === 'dropdown' && (
                          <select disabled className="preview-input">
                            <option>-- Select --</option>
                            {q.options.map(opt => <option key={opt}>{opt}</option>)}
                          </select>
                        )}
                        {q.type === 'multi-select' && (
                          <div className="preview-options">
                            {q.options.map(opt => (
                              <label key={opt} className="preview-option-label">
                                <input type="checkbox" disabled /> {opt}
                              </label>
                            ))}
                          </div>
                        )}
                        {q.type === 'file' && <input type="file" disabled className="preview-input" />}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <div className="job-preview-visibility">
              <strong>Visible to:</strong> {jobForm.visibleTo.length ? jobForm.visibleTo.join(', ') : 'No tiers selected'}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default CreateJobPage;
